import * as React from 'react';
import { useCallback, useImperativeHandle, useState } from 'react';
import { Formik, Form, Field } from 'formik';

import { ModalSubmit, Spinner } from 'components/common';
import { HelpBlock, Modal } from 'components/bootstrap';
import useCollectionMutation from 'collections/hooks/useCollectionMutation';
import { createGRN, getValuesFromGRN } from 'logic/permissions/GRN';
import useEntityCollections from 'collections/hooks/useEntityCollections';

import AddCollectionFormGroup from './AddCollectionFormGroup';

type FormValues = { selected_collections: Array<string> };

type Props = {
  title: string;
  entityId: string;
  entityType: string;
  submitButtonText?: string;
  submitLoadingText?: string;
  show?: boolean;
  onClose?: () => void;
};

const getRequestPayload = (selectedCollections: FormValues['selected_collections'], initialValues: Array<string>) => {
  const newValues = selectedCollections?.filter((str) => str !== '') || [];
  const prevOptions = initialValues;
  const added = newValues?.filter((val) => !prevOptions.some((prev) => prev === val)) ?? [];
  const removed = prevOptions?.filter((val) => !newValues.some((curr) => curr === val)) ?? [];

  return { addTo: added.map((a) => getValuesFromGRN(a).id), removeFrom: removed.map((r) => getValuesFromGRN(r).id)};
};

const EntityCollectionModal = React.forwardRef(
  (
    {
      entityId,
      entityType,
      title: modalTitle,
      submitButtonText = 'Save',
      submitLoadingText = 'Saving...',
      show = false,
      onClose = () => {},
    }: React.PropsWithoutRef<Props>,
    ref: React.MutableRefObject<any>,
  ) => {
    const [showModal, setShowModal] = useState(show);
    const entityGRN = createGRN(entityType, entityId);
    const { data: entityCollections, isLoading } = useEntityCollections([entityGRN], { enabled: showModal });
    const { updateEntityCollectionsMutation } = useCollectionMutation();

    useImperativeHandle(ref, () => ({
      toggle: () => setShowModal(true),
    }));

    const closeModal = useCallback(() => {
      setShowModal(false);
      if (onClose) onClose();
    }, [onClose]);

    const initialValues: FormValues = {
      selected_collections: entityCollections?.[entityId]?.map((collection) => createGRN('collection', collection.id)),
    };

    const handleSubmit = useCallback(
      (values: FormValues) =>
        updateEntityCollectionsMutation({
          entityId: createGRN(entityType, entityId),
          payload: getRequestPayload(values.selected_collections, initialValues.selected_collections),
        }).then(() => closeModal()),
      [entityId, entityType, closeModal, updateEntityCollectionsMutation, initialValues.selected_collections],
    );

    return (
      <Modal onHide={closeModal} show={showModal}>
        {isLoading ? (
          <Spinner text="Loading entity collections." />
        ) : (
          <Formik<FormValues> initialValues={initialValues} onSubmit={handleSubmit}>
            {({ isSubmitting, isValidating, setFieldValue }) => (
              <Form>
                <Modal.Header>
                  <Modal.Title>{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Field name="selected_collections">
                    {({ field: { value } }) => (
                       <AddCollectionFormGroup
                         label=""
                         hideHelp
                         entityType={entityType}
                         onChange={(selected) => setFieldValue('selected_collections', selected.selected_collections)}
                         value={value}
                       />
                      )}
                  </Field>
                  <HelpBlock>{`Select the collections in which this ${entityType} will be added to. The ${entityType} will be removed from collections you deselect. You can create collections in the collections page`}</HelpBlock>
                </Modal.Body>
                <Modal.Footer>
                  <ModalSubmit
                    submitButtonText={submitButtonText}
                    submitLoadingText={submitLoadingText}
                    onCancel={closeModal}
                    disabledSubmit={isValidating}
                    isSubmitting={isSubmitting}
                  />
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    );
  },
);

export default EntityCollectionModal;
